import { useReducedMotion } from '@mantine/hooks';
import { Line } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useRef } from 'react';
import { MathUtils, type Group } from 'three';

import {
    INTERACTION_RENDER_ORDER,
    INTERACTION_LINE_WIDTH_PX,
    INTERACTION_HALO_WIDTH_PX
} from 'app/modules/wallforming/components/scenes/objects/utils/screenSpaceUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { PRIMARY_COLOR_ACCENT_HEX } from 'shared/constants';

import type { Line2 } from 'three-stdlib';

const noRaycast = () => {};
const INITIAL_POINTS: [number, number, number][] = [
    [0, 0, 0],
    [0, 0, 0]
];

export const SnapEdgeMarker = () => {
    // Store updates must wake demand-rendered canvases, including on release.
    usePlacementStore((state) => state.snapEdgeMarker);
    const groupRef = useRef<Group | null>(null);
    const haloRef = useRef<Line2 | null>(null);
    const lineRef = useRef<Line2 | null>(null);
    const previousEdgeRef = useRef(usePlacementStore.getState().snapEdgeMarker);
    const reducedMotion = useReducedMotion();

    useFrame(({ invalidate }, delta) => {
        const group = groupRef.current;
        const line = lineRef.current;
        const halo = haloRef.current;
        if (!group || !line || !halo) {
            return;
        }
        const edge = usePlacementStore.getState().snapEdgeMarker;
        if (edge && (edge !== previousEdgeRef.current || !group.visible)) {
            const positions = [...edge.start, ...edge.end];
            line.geometry.setPositions(positions);
            halo.geometry.setPositions(positions);
        }
        previousEdgeRef.current = edge;
        const opacity = edge ? 1 : 0;
        line.material.opacity = reducedMotion ? opacity : MathUtils.damp(line.material.opacity, opacity, 20, delta);
        if (Math.abs(line.material.opacity - opacity) < 0.01) {
            line.material.opacity = opacity;
        } else {
            invalidate();
        }
        halo.material.opacity = line.material.opacity;
        group.visible = line.material.opacity > 0;
    });

    return (
        <group ref={groupRef} name="snap-edge-feedback" visible={false}>
            <Line
                ref={haloRef}
                color="white"
                depthTest={false}
                depthWrite={false}
                lineWidth={INTERACTION_HALO_WIDTH_PX}
                opacity={0}
                points={INITIAL_POINTS}
                raycast={noRaycast}
                renderOrder={INTERACTION_RENDER_ORDER}
                toneMapped={false}
                transparent
            />
            <Line
                ref={lineRef}
                color={PRIMARY_COLOR_ACCENT_HEX}
                depthTest={false}
                depthWrite={false}
                lineWidth={INTERACTION_LINE_WIDTH_PX}
                opacity={0}
                points={INITIAL_POINTS}
                raycast={noRaycast}
                renderOrder={INTERACTION_RENDER_ORDER + 1}
                toneMapped={false}
                transparent
            />
        </group>
    );
};
